/**
 * Select Component
 *
 * This component provides a Select dropdown, leveraging MUI's Autocomplete.
 * The Select can be configured to allow multiple selections or single selections.
 * Additionally, it supports grouping of options and a placeholder.
 *
 * When an option is selected, the 'onSelectedOption' callback is fired with
 * the selected option(s) as its argument.
 *
 * @component
 * @param {boolean} multi - Multiple selections allowed.
 * @param {function} onTextChange - Callback for text change event.
 * @param {function} onSelectedOption - Callback for option selection.
 * @param {string} placeholder - Placeholder text.
 * @param {boolean} required - Selection is required.
 * @param {Array} value - Current selected value(s).
 * @param {boolean} grouped - Options should be grouped.
 * @param {Array} options - Option objects for selection.
 *
 *  @example options = [{ label: 'Option 1', value: 1 }, { label: 'Option 2', value: 2 }]
 *  @example groupedOptions = [{ label: 'Option 1', value: 1, groupLabel: 'Group 1' }, { label: 'Option 2', value: 2, groupLabel: 'Group 2' }]
 *
 * @param {Object} rest - Any additional props to be passed to the component.
 */

import { useEffect, useState } from 'react';
import { Autocomplete, autocompleteClasses, Box, buttonBaseClasses, Checkbox, Chip, Icon, ListItem, TextField } from '@mui/material';

import isEmpty from 'Lib/utilities/isEmpty';

export default function Select(props) {
    const { multi, onTextChange, onSelectedOption, placeholder, required, value, grouped, disabled, getOptionLabel = option => option.label, ...rest } = props;
    const [selectedOption, setSelectedOption] = useState(multi ? [] : null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (multi) {
            const valueArray = !Array.isArray(value) ? [value] : value;
            const options = valueArray?.length ? props.options.filter(option => valueArray.includes(option.value.toString())) : [];
            setSelectedOption(options);
        } else {
            const option = props.options.find(option => option.value == value) ?? null;
            setSelectedOption(option);
        }
    }, [value]);

    const handleValueChange = (event, val) => {
        onSelectedOption(val);
        setSelectedOption(val);
    };

    return (
        <Autocomplete
            {...rest}
            id="select"
            open={open}
            multiple={multi}
            value={selectedOption}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            disabled={disabled}
            onChange={handleValueChange}
            getLimitTagsText={amount => `+${amount} more`}
            groupBy={option => (grouped ? option.groupLabel : null)}
            renderOption={(optionProps, option, { selected }) => (
                <ListItem {...optionProps} key={optionProps.id} sx={{ backgroundColor: 'unset', padding: multi ? '0px !important' : '12px !important' }}>
                    {multi && <Checkbox icon={<Icon>check_box_outline_blank</Icon>} checkedIcon={<Icon>check_box</Icon>} style={{ marginRight: 8 }} checked={selected} />}
                    {getOptionLabel(option)}
                </ListItem>
            )}
            getOptionLabel={getOptionLabel}
            renderTags={(value, getTagProps) => value.map((option, index) => <Chip key={index} label={option.label} deleteIcon={<Icon>close</Icon>} {...getTagProps({ index })} />)}
            renderInput={params => {
                const showPlaceholder = isEmpty(params.InputProps.startAdornment);

                return (
                    <TextField
                        {...params}
                        label={rest.label}
                        autoComplete="off"
                        variant="outlined"
                        placeholder={showPlaceholder ? `${placeholder}${required ? '*' : ''}` : ''}
                        onChange={onTextChange}
                        helperText={
                            required ? (
                                <Box component="span">
                                    <span className="required">*</span>
                                    Required
                                </Box>
                            ) : null
                        }
                        sx={{
                            [`& .${autocompleteClasses.endAdornment}`]: { top: 'unset', [`& .${buttonBaseClasses.root}`]: { width: '24px', height: '24px' } }
                        }}
                        InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
                        InputProps={{
                            className: 'field',
                            ...params.InputProps,
                            startAdornment: <>{params.InputProps.startAdornment}</>,
                            endAdornment: (
                                <Icon sx={disabled ? {} : { cursor: 'pointer' }} onClick={() => !disabled && setOpen(!open)}>
                                    {open ? 'expand_less' : 'expand_more'}
                                </Icon>
                            )
                        }}
                    />
                );
            }}
        />
    );
}
